import DeleteIcon from "@mui/icons-material/Delete";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import {
  Alert,
  Button,
  Divider,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Vehicle } from "../types";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

interface Props {
  vehicles: Vehicle[];
  onChange: (vehicles: Vehicle[]) => void;
  updateError?: FetchBaseQueryError | SerializedError | undefined;
}

interface VehicleRowProps {
  vehicle: Vehicle;
  onDeleteClick: () => void;
}

function VehicleRow({ vehicle, onDeleteClick }: VehicleRowProps) {
  const { t } = useTranslation();

  return (
    <ListItem>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        width="100%"
      >
        <Stack direction="row" spacing={2}>
          <DirectionsCarIcon aria-hidden />
          <Typography>{vehicle.licence_plate}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            title={t("vehiclesForm.delete", {
              licensePlate: vehicle.licence_plate,
            })}
            onClick={onDeleteClick}
          >
            <DeleteIcon />
          </Button>
        </Stack>
      </Stack>
    </ListItem>
  );
}

export default function Vehicles({ vehicles, onChange, updateError }: Props) {
  const { t } = useTranslation();

  const [newLicensePlate, setNewLicencePlate] = useState("");

  const onAdd = () => {
    onChange([...vehicles, { licence_plate: newLicensePlate }]);
    setNewLicencePlate("");
  };

  const onRemove = (index: number) => {
    onChange(vehicles.filter((_, i) => i !== index));
  };

  const isAddVehicleDisabled =
    vehicles.length > 2 || newLicensePlate.trim().length === 0;

  return (
    <Stack
      border={1}
      borderColor="divider"
      borderRadius={4}
      padding={{ xs: 2, sm: 4 }}
      spacing={2}
      width="100%"
      sx={{ backgroundColor: "white" }}
    >
      <Typography variant="h5">{t("vehiclesForm.title")}</Typography>
      <Typography variant="subtitle1">{t("vehiclesForm.subtitle")}</Typography>
      <List>
        {vehicles.map((vehicle, index) => (
          <Fragment key={vehicle.licence_plate}>
            <VehicleRow
              vehicle={vehicle}
              onDeleteClick={() => {
                onRemove(index);
              }}
            />
            {index < vehicles.length - 1 ? <Divider component="li" /> : null}
          </Fragment>
        ))}
      </List>
      {updateError && (
        <Alert data-testid="user-details-add-vehicle-alert" severity="error">
          {t("unknownError")}
        </Alert>
      )}
      <TextField
        slotProps={{ htmlInput: { readOnly: vehicles.length > 2 } }}
        required
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onAdd();
          }
        }}
        label={t("vehiclesForm.addInputLabel")}
        value={newLicensePlate}
        onChange={(event) => {
          setNewLicencePlate(event.target.value);
        }}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={onAdd}
        disabled={isAddVehicleDisabled}
      >
        {t("vehiclesForm.add")}
      </Button>
    </Stack>
  );
}
